<script setup>
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import { ChevronUpIcon } from '@heroicons/vue/20/solid'
import { onMounted, ref } from 'vue'

const props = defineProps({
    show_faqs: {
        type: Array,
        required: false,
        default: () => [],
    },

    selectionWidth: {
        type: String,
        default: 'max-w-4xl',
    },
})

const faq_questions = [
    {
        name: 'Výhody foukané izolace Supafil',
        slug: 'vyhody-supafil',
        questions: [
            {
                title: 'Jaké se používají k zateplení foukanou izolací druhy materiálů?',
                answer: 'K nejpoužívanějším patří foukaná minerální vlna, skelná, kamenná nebo foukaná celulóza. My pracujeme s foukanou izolací Supafil ze skelné minerální vlny bez pojiva tzv. panenské vlákno. Je bílá a neobsahuje žádné škodlivé chemické látky.',
            },
            {
                title: 'Jaké jsou akustické vlastnosti?',
                answer: 'Všechny vláknité izolace jsou zvukově vysoce pohltivé. Izolace z panenského vlákna Supafil slouží tedy nejen jako tepelný, ale i zvukový izolant. Stříkané izolace materiály jako PUR, PIR naopak daleko lépe vedou zvuk a tedy mají horší akustické vlastnosti.',
            },
            {
                title: 'Co znamená, že izolace nesesedá?',
                answer: 'Některé izolace přirozeně v průběhu času sesedají až o 10% dle výrobce a aplikace. Foukaná minerální izolace Supafil sesedá o méně než 1%. Sesedání není tedy v podstatě měřitelné a není třeba navyšovat tloušťku izolace kvůli sesednutí.',
            },
            {
                title: 'Může izolaci poškodit vlhkost?',
                answer: 'Izolace Supafil neobsahuje pojiva, která by vlhkost poškodila. Případná vlhkost způsobená srážením nebo zatečením samovolně z materiálu vysychá, aniž by ho poškodila.',
            },
        ],
    },
    {
        name: 'Technické parametry a aplikace',
        slug: 'technicke-parametry',
        questions: [
            {
                title: 'Jaká je izolační schopnost izolace Supafil?',
                answer: 'Izolační schopnost minerální vaty Supafil je srovnatelná s běžnými skelnými a čedičovými izolacemi. Její účinnost je závislá na hustotě nafoukání tj. objemové hmotnosti. Pro docílení potřebné míry zateplení se obvykle ukládá v tloušťce 200 - 400 mm podle typu konstrukce.',
            },
            {
                title: 'Zatížení konstrukce',
                answer: 'Zatížení konstrukcí izolací je důležitým parametrem hlavně u zavěšených sádrokartonových podhledů, u bungalovů, kde nejlepším řešením zateplení jsou právě foukané izolace. Při obvyklé tloušťce zateplení 30 mm je zatížení 6kg/m².',
            },
        ],
    },
]

const show_faq = ref(faq_questions[0])

onMounted(() => {
    if (props.show_faqs) {
        show_faq.value = faq_questions.find((faq) => faq.slug === props.show_faqs[0])
    }
})
</script>

<template>
    <section
        id="faq"
        aria-label="Programátorské akce zaměřené na Laravel, Inertia.js, Vue.js a Tailwind CSS"
        class="scroll-mt-32"
    >
        <div :class="'mx-auto w-full ' + selectionWidth">
            <h2 class="font-chivo text-center text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl">
                Často kladené otázky
            </h2>

            <div
                class="mt-2 flex flex-col items-center justify-center gap-5 rounded-4xl bg-black/50 p-2 lg:mt-5 lg:flex-row lg:gap-2 lg:rounded-full"
            >
                <template
                    v-for="faq_question in faq_questions"
                    :key="faq_question.slug"
                >
                    <button
                        v-if="show_faqs ? show_faqs.includes(faq_question.slug) : true"
                        @click="show_faq = faq_question"
                        :arial-label="'Tlačítko ' - faq_question.name"
                        :class="[
                            show_faq.slug === faq_question.slug ? 'bg-brand-orange' : 'hover:bg-brand-orange',
                            'w-full cursor-pointer rounded-3xl px-5 py-4 text-center text-white transition-all duration-100 ease-linear lg:rounded-full xl:py-2',
                        ]"
                    >
                        <span class="font-literata text-center text-xl 2xl:text-2xl">
                            {{ faq_question.name }}
                        </span>
                    </button>
                </template>
            </div>
        </div>

        <div class="mt-5 space-y-1 rounded-4xl bg-black/50 p-2 lg:mt-2 lg:rounded-2xl">
            <Disclosure
                v-for="(faq, index) in show_faq.questions"
                :key="index"
                v-slot="{ open }"
            >
                <DisclosureButton
                    :class="[
                        open ? 'bg-brand-orange text-white' : 'bg-transparent text-white',
                        'group hover:bg-brand-orange flex w-full cursor-pointer justify-between rounded-3xl px-4 py-2 text-left focus:outline-none lg:rounded-lg',
                    ]"
                >
                    <span class="font-chivo text-xl font-medium hover:text-white">{{ faq.title }}</span>
                    <ChevronUpIcon
                        :class="[
                            open ? 'rotate-180 transform text-white' : 'text-brand-orange group-hover:text-white',
                            'size-6 shrink-0',
                        ]"
                    />
                </DisclosureButton>

                <Transition
                    enter-active-class="transition duration-100 ease-out"
                    enter-from-class="transform scale-95 opacity-0"
                    enter-to-class="transform scale-100 opacity-100"
                    leave-active-class="transition duration-75 ease-out"
                    leave-from-class="transform scale-100 opacity-100"
                    leave-to-class="transform scale-95 opacity-0"
                >
                    <DisclosurePanel
                        class="border-brand-orange mb-2 ml-4 border-l px-4 pt-4 pb-2 text-sm text-gray-100"
                    >
                        {{ faq.answer }}
                    </DisclosurePanel>
                </Transition>
            </Disclosure>
        </div>
    </section>
</template>
